import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CloudService, LambdaFunction, LambdaMetrics, LambdaFunctionMetrics, LambdaHourSlot, ServiceCardData } from '../../core/services/cloud.service';
import { ToastService } from '../../core/services/toast.service';
import { MonitoringService } from '../../core/services/monitoring.service';
import {
  CloudFilters,
  FacetDef,
  SortOption,
  parseFiltersFromParams,
  filtersToParams,
} from './cloud-filter.model';
import { ActivityItem, findingsToActivity, sortActivity } from './cloud-activity.model';

type Tab = 'overview' | 'functions' | 'activity';

const DEFAULT_SORT = 'modified-desc';
const FACET_KEYS   = ['status', 'runtime', 'monitoring'];

const SORT_OPTIONS: SortOption[] = [
  { value: 'modified-desc',    label: 'Latest modified'  },
  { value: 'issues-desc',      label: 'Most issues'      },
  { value: 'name-asc',         label: 'Name A → Z'       },
  { value: 'name-desc',        label: 'Name Z → A'       },
  { value: 'memory-desc',      label: 'Most memory'      },
  { value: 'monitored-first',  label: 'Monitored first'  },
];

@Component({
  selector: 'app-lambda-detail',
  templateUrl: './lambda-detail.component.html',
})
export class LambdaDetailComponent implements OnInit, OnDestroy {
  selectedTab: Tab = 'overview';
  functions: LambdaFunction[] = [];
  loading = true;

  metrics: LambdaMetrics | null = null;
  metricsState: 'loading' | 'loaded' | 'unavailable' = 'loading';

  readonly pageSize    = 10;
  currentPage          = 1;
  filters: CloudFilters = { query: '', facets: {}, sort: DEFAULT_SORT };

  readonly tabs: { id: Tab; label: string }[] = [
    { id: 'overview',  label: 'Overview'  },
    { id: 'functions', label: 'Functions' },
    { id: 'activity',  label: 'Activity'  },
  ];

  readonly sortOptions = SORT_OPTIONS;

  readonly serviceCard: ServiceCardData = { id: 'lambda', name: 'Lambda', icon: 'zap', route: '/cloud/lambda', status: 'healthy', metrics: [] };

  selectedFunction: string | null = null;
  fnMetrics: LambdaFunctionMetrics | null = null;

  drawerItemId: string | null = null;
  fnMetricsState: 'loading' | 'loaded' | 'unavailable' = 'loading';
  fnSlotsPage = 1;
  readonly fnSlotsPageSize = 10;

  activity: ActivityItem[] = [];
  activityLoading = true;
  refreshing = false;

  private sub?: Subscription;
  private metricsSub?: Subscription;
  private routeSub?: Subscription;
  private fnMetricsSub?: Subscription;
  private findingsSub?: Subscription;

  constructor(
    private readonly cloud: CloudService,
    private readonly toast: ToastService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly monitoring: MonitoringService,
  ) {}

  ngOnInit(): void {
    this.routeSub = this.route.queryParams.subscribe(params => {
      const newFilters = parseFiltersFromParams(params as Record<string, string>, DEFAULT_SORT, FACET_KEYS);
      if (JSON.stringify(newFilters) !== JSON.stringify(this.filters)) {
        this.currentPage = 1;
      }
      this.filters = newFilters;

      const fnName = (params['fn'] as string | undefined) ?? null;
      if (fnName !== this.selectedFunction) {
        this.selectedFunction = fnName;
        this.fnSlotsPage = 1;
        if (fnName) {
          this.loadFunctionMetrics();
        } else {
          this.fnMetrics      = null;
          this.fnMetricsState = 'loading';
          this.fnMetricsSub?.unsubscribe();
        }
      }
    });
    this.cloud.loadItemPoliciesForService('lambda').subscribe();
    this.loadData(false);
  }

  /** Loads the function list + account metrics (cache-aware), monitoring state (monitored count), and activity findings. */
  private loadData(bypass: boolean): void {
    this.sub?.unsubscribe();
    this.sub = this.cloud.getLambdaFunctions(bypass).subscribe(fns => {
      this.functions  = fns;
      this.loading    = false;
      this.refreshing = false;
    });
    this.metricsSub?.unsubscribe();
    this.metricsSub = this.cloud.getLambdaMetrics().subscribe(m => {
      this.metrics      = m;
      this.metricsState = m !== null ? 'loaded' : 'unavailable';
    });
    this.cloud.loadMonitoringState().subscribe();
    this.findingsSub?.unsubscribe();
    this.findingsSub = this.monitoring.getFindings().subscribe(findings => {
      this.activityLoading = false;
      this.activity        = sortActivity(findingsToActivity(findings, 'LAMBDA'));
    });
  }

  /** Manual refresh — bypass the cache and reload (guarded against double-clicks). */
  refresh(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.loadData(true);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.metricsSub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.fnMetricsSub?.unsubscribe();
    this.findingsSub?.unsubscribe();
  }

  onActivitySelect(item: ActivityItem): void {
    this.selectedTab = 'functions';
    this.selectFunction(item.resourceId);
  }

  // ── Summary counts (standardized: total + monitored) ────────────────────────

  get totalCount():     number { return this.functions.length; }
  get monitoredCount(): number { return this.functions.filter(f => this.cloud.isMonitored('lambda', f.name)).length; }

  // ── Facets (dynamic: runtime list derived from data) ────────────────────────

  private _facetsRef: LambdaFunction[] | null = null;
  private _facetsCache: FacetDef[] = [];

  get facets(): FacetDef[] {
    if (this._facetsRef === this.functions) return this._facetsCache;
    this._facetsRef = this.functions;
    const runtimes = [...new Set(this.functions.map(f => f.runtime))].sort();
    this._facetsCache = [
      {
        key: 'status',
        label: 'Status',
        options: [
          { value: 'healthy', label: 'Healthy'     },
          { value: 'warning', label: 'Old runtime' },
          { value: 'unknown', label: 'Unknown'     },
        ],
      },
      {
        key: 'runtime',
        label: 'Runtime',
        options: runtimes.map(r => ({ value: r, label: r })),
      },
      {
        key: 'monitoring',
        label: 'Monitoring',
        options: [
          { value: 'monitored',   label: 'Monitored'     },
          { value: 'unmonitored', label: 'Not monitored' },
        ],
      },
    ];
    return this._facetsCache;
  }

  // ── Filtered + sorted list ──────────────────────────────────────────────────

  get filteredFunctions(): LambdaFunction[] {
    let result = this.functions;

    const q = this.filters.query.toLowerCase().trim();
    if (q) result = result.filter(f => f.name.toLowerCase().includes(q));

    const statuses = this.filters.facets['status'] ?? [];
    if (statuses.length) result = result.filter(f => statuses.includes(f.status));

    const runtimes = this.filters.facets['runtime'] ?? [];
    if (runtimes.length) result = result.filter(f => runtimes.includes(f.runtime));

    const monitoring = this.filters.facets['monitoring'] ?? [];
    if (monitoring.length) {
      result = result.filter(f => {
        const mon = this.cloud.isMonitored('lambda', f.name);
        return (monitoring.includes('monitored') && mon) || (monitoring.includes('unmonitored') && !mon);
      });
    }

    return this.sorted(result, this.filters.sort);
  }

  private sorted(fns: LambdaFunction[], sort: string): LambdaFunction[] {
    return [...fns].sort((a, b) => {
      switch (sort) {
        case 'name-asc':    return a.name.localeCompare(b.name);
        case 'name-desc':   return b.name.localeCompare(a.name);
        case 'memory-desc': return b.memoryMB - a.memoryMB;
        case 'issues-desc':
          // Primary: errors descending; secondary: throttles descending
          if (b.errors24h !== a.errors24h) return b.errors24h - a.errors24h;
          return b.throttles24h - a.throttles24h;
        case 'monitored-first': {
          const aM = this.cloud.isMonitored('lambda', a.name) ? 0 : 1;
          const bM = this.cloud.isMonitored('lambda', b.name) ? 0 : 1;
          return aM - bM;
        }
        default:            return +new Date(b.lastModified) - +new Date(a.lastModified);
      }
    });
  }

  selectFunction(name: string): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         { fn: name },
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  closeFunction(): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         { fn: null },
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  private loadFunctionMetrics(): void {
    if (!this.selectedFunction) return;
    this.fnMetricsState = 'loading';
    this.fnMetricsSub?.unsubscribe();
    this.fnMetricsSub = this.cloud.getLambdaFunctionMetrics(this.selectedFunction).subscribe({
      next: (m) => {
        this.fnMetrics      = m;
        this.fnMetricsState = m !== null ? 'loaded' : 'unavailable';
        this.fnSlotsPage    = Math.min(this.fnSlotsPage, Math.max(1, this.fnSlotsTotalPages));
      },
      error: () => {
        this.fnMetrics      = null;
        this.fnMetricsState = 'unavailable';
      },
    });
  }

  get selectedFunctionData(): LambdaFunction | null {
    return this.functions.find(f => f.name === this.selectedFunction) ?? null;
  }

  get fnSlotsTotalPages(): number {
    return Math.max(1, Math.ceil((this.fnMetrics?.slots.length ?? 0) / this.fnSlotsPageSize));
  }

  get pagedFnSlots(): LambdaHourSlot[] {
    const slots = this.fnMetrics?.slots ?? [];
    const start = (this.fnSlotsPage - 1) * this.fnSlotsPageSize;
    return slots.slice(start, start + this.fnSlotsPageSize);
  }

  formatMs(ms: number | null): string {
    if (ms === null) return '—';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  }

  onFiltersChange(f: CloudFilters): void {
    this.currentPage = 1;
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         filtersToParams(f, FACET_KEYS),
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  // ── Pagination ──────────────────────────────────────────────────────────────

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredFunctions.length / this.pageSize));
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  get pagedFunctions(): LambdaFunction[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredFunctions.slice(start, start + this.pageSize);
  }

  get pageStart(): number {
    return this.filteredFunctions.length === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }

  get pageEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.filteredFunctions.length);
  }

  goToPage(page: number): void {
    this.currentPage = Math.max(1, Math.min(page, this.totalPages));
  }

  prevPage(): void { this.goToPage(this.currentPage - 1); }
  nextPage(): void { this.goToPage(this.currentPage + 1); }

  // ── Monitoring quota ────────────────────────────────────────────────────────

  get quotaUsage() { return this.cloud.getQuotaUsage(); }
  get canMonitorMore(): boolean { return this.cloud.canEnableMoreMonitoring(); }

  isMonitored(name: string): boolean { return this.cloud.isMonitored('lambda', name); }

  toggleMonitoring(name: string): void {
    const current = this.cloud.isMonitored('lambda', name);
    if (!current && !this.cloud.canEnableMoreMonitoring()) {
      this.toast.show(this.cloud.getQuotaExceededMessage(), 'error');
      return;
    }
    this.cloud.toggleMonitoring('lambda', name, !current).subscribe({
      error: err => {
        if (err.status === 403) {
          const msg = err.error?.code === 'QUOTA_EXCEEDED'
            ? this.cloud.getQuotaExceededMessage()
            : 'Only org owners and admins can configure monitoring.';
          this.toast.show(msg, 'error');
        }
      },
    });
  }

  // ── Item settings drawer ────────────────────────────────────────────────────

  openItemSettings(name: string): void  { this.drawerItemId = name; }
  closeItemSettings(): void             { this.drawerItemId = null; }

  itemOverrideSummary(name: string): string {
    return this.cloud.getItemOverrideSummary('lambda', name);
  }

  // ── Overview metric formatting ──────────────────────────────────────────────

  formatCount(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
    return String(n);
  }
}
